import axios, { AxiosInstance } from 'axios';
import type { CourseOut, LessonListItem, PageListItem, PageDetailOut } from '../types/catalog';
import { getAuthHeaders, getLessonTenantIdFallbacks } from '../../../utils/apiHeaders';

/** Base URL from env. Default to gateway (8080) */
const BASE_URL = import.meta.env.VITE_GATEWAY_URL || 'http://localhost:8080';

const createClient = (): AxiosInstance => {
  return axios.create({
    baseURL: BASE_URL,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
  });
};

export interface ListCoursesParams {
  grade?: number;
  book?: string;
}

export const listCourses = async (params?: ListCoursesParams): Promise<CourseOut[]> => {
  const client = createClient();
  const response = await client.get<CourseOut[]>('/v1/courses', {
    params: params ? { grade: params.grade, book: params.book } : {},
  });
  return Array.isArray(response.data) ? response.data : [];
};

/** GET /v1/courses/:courseId — single course (for grade, etc.) */
export const getCourse = async (courseId: string): Promise<CourseOut | null> => {
  const client = createClient();
  try {
    const response = await client.get<CourseOut>(`/v1/courses/${courseId}`);
    return response.data ?? null;
  } catch {
    return null;
  }
};

export const listLessons = async (courseId: string): Promise<LessonListItem[]> => {
  const client = createClient();
  const tenantFallbacks = getLessonTenantIdFallbacks();

  for (const tenantId of tenantFallbacks) {
    try {
      // Override the tenant header for this request
      const response = await client.get<LessonListItem[]>(`/v1/courses/${courseId}/lessons`, {
        headers: {
          'x-tenant-id': tenantId,
        },
      });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error: any) {
      // If it's a 401, 404, or 422, try the next tenant ID
      if (error.response?.status === 401 || error.response?.status === 404 || error.response?.status === 422) {
        console.warn(`Lessons request failed with tenant "${tenantId}", trying fallback...`);
        continue;
      }
      // For other errors, throw immediately
      throw error;
    }
  }

  // If all tenant fallbacks failed
  throw new Error(`Failed to fetch lessons for course ${courseId} with all tenant fallbacks`);
};

export const listPages = async (lessonId: string): Promise<PageListItem[]> => {
  const client = createClient();
  const response = await client.get<PageListItem[]>(`/v1/lessons/${lessonId}/pages`);
  return Array.isArray(response.data) ? response.data : [];
};

export const getPage = async (pageId: string): Promise<PageDetailOut> => {
  const client = createClient();
  const response = await client.get<PageDetailOut>(`/v1/pages/${pageId}`);
  return response.data;
};
